import React, { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartPie,
  faSpinner,
  faInfoCircle,
  faDownload,
  faExpand,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, Title, ChartDataLabels);

const EnhancedReferencesPerOperationTypeDonutChart = ({
  data,
  isLoading,
  onChartItemClick,
  height = "100%",
}) => {
  // Define a modern color palette for the segments
  const segmentColors = [
    "rgba(54, 162, 235, 0.8)", // Blue
    "rgba(255, 99, 132, 0.8)", // Pink
    "rgba(75, 192, 192, 0.8)", // Teal
    "rgba(255, 159, 64, 0.8)", // Orange
    "rgba(153, 102, 255, 0.8)", // Purple
    "rgba(255, 205, 86, 0.8)", // Yellow
    "rgba(201, 203, 207, 0.8)", // Gray
  ];

  // State for chart data
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [],
        borderColor: [],
        borderWidth: 1,
        hoverOffset: 15,
      },
    ],
  });

  // State for chart information
  const [chartInfo, setChartInfo] = useState({
    total: 0,
    highest: { key: "", value: 0, percentage: 0 },
    operationTypes: [],
  });

  // State for expanded chart view
  const [isExpanded, setIsExpanded] = useState(false);

  // Helper function to get operation type text
  const getOperationTypeText = (operationType) => {
    if (operationType === "1") return "Import";
    if (operationType === "2") return "Export";
    return operationType || "Unknown";
  };

  // Process chart data when the input data changes
  useEffect(() => {
    if (isLoading || !data || data.length === 0) {
      return;
    }

    const processData = () => {
      // Count references by operation type
      const operationTypeCounts = {};
      data.forEach((item) => {
        const operationType = getOperationTypeText(item.header.operation_type);
        if (!operationType) return;
        operationTypeCounts[operationType] =
          (operationTypeCounts[operationType] || 0) + 1;
      });

      // Sort operation types by count (descending)
      const sortedOperationTypes = Object.entries(operationTypeCounts)
        .sort((a, b) => b[1] - a[1])
        .reduce(
          (acc, [key, value]) => {
            acc.keys.push(key);
            acc.values.push(value);
            return acc;
          },
          { keys: [], values: [] }
        );

      // Calculate total
      const total = sortedOperationTypes.values.reduce(
        (sum, val) => sum + val,
        0
      );

      // Find highest value and calculate percentages
      let highest = { key: "", value: 0, percentage: 0 };
      const operationTypesInfo = [];

      sortedOperationTypes.keys.forEach((key, index) => {
        const value = sortedOperationTypes.values[index];
        const percentage = ((value / total) * 100).toFixed(1);

        operationTypesInfo.push({
          name: key,
          count: value,
          percentage: percentage,
          color: segmentColors[index % segmentColors.length],
        });

        if (value > highest.value) {
          highest = {
            key,
            value,
            percentage,
          };
        }
      });

      // Update chart data state
      setChartData({
        labels: sortedOperationTypes.keys,
        datasets: [
          {
            data: sortedOperationTypes.values,
            backgroundColor: sortedOperationTypes.keys.map(
              (_, index) => segmentColors[index % segmentColors.length]
            ),
            borderColor: sortedOperationTypes.keys.map((_, index) =>
              segmentColors[index % segmentColors.length].replace("0.8", "1")
            ),
            borderWidth: 1,
            hoverOffset: 15,
          },
        ],
      });

      // Update chart info
      setChartInfo({
        total,
        highest,
        operationTypes: operationTypesInfo,
      });
    };

    processData();
  }, [data, isLoading]);

  // Chart options configuration
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "65%",
    layout: {
      padding: 20,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#333",
        bodyColor: "#333",
        bodyFont: {
          size: 12,
        },
        borderColor: "rgba(200, 200, 200, 0.9)",
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        usePointStyle: true,
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ${value} (${percentage}%)`;
          },
        },
      },
      datalabels: {
        formatter: (value, context) => {
          const total = context.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = ((value / total) * 100).toFixed(0);
          // Only show percentage for segments that are big enough
          return percentage > 5 ? `${percentage}%` : "";
        },
        color: "#fff",
        font: {
          weight: "bold",
          size: 11,
        },
        textStrokeColor: "rgba(0, 0, 0, 0.2)",
        textStrokeWidth: 2,
        // Only display data labels for larger segments
        display: (context) => {
          const value = context.dataset.data[context.dataIndex];
          const total = context.dataset.data.reduce((a, b) => a + b, 0);
          return value / total > 0.05; // Only show labels for segments > 5%
        },
      },
    },
    onClick: (_, elements) => {
      if (elements.length > 0 && typeof onChartItemClick === "function") {
        const index = elements[0].index;
        const operationType = chartData.labels[index];
        const count = chartData.datasets[0].data[index];
        onChartItemClick({
          type: "operationType",
          value: operationType,
          count: count,
        });
      }
    },
  };

  // Render loading spinner when data is loading
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <FontAwesomeIcon
          icon={faSpinner}
          className="animate-spin text-xl text-orange-600 mr-2"
        />
        <span className="text-gray-600">Loading chart data...</span>
      </div>
    );
  }

  // Render "no data" message when there is no data
  if (!data || data.length === 0 || chartData.labels.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-full">
        <FontAwesomeIcon
          icon={faChartPie}
          className="text-2xl text-gray-400 mb-2"
        />
        <span className="text-gray-500">No data available for this chart</span>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full h-full flex flex-col">
      {/* Chart Header */}
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faChartPie} className="text-orange-600 mr-2" />
          References per Operation Type
        </h2>
      </div>

      {/* Chart Container */}
      <div className="flex-grow relative">
        <div className="relative h-full flex flex-col md:flex-row items-center">
          {/* Donut Chart */}
          <div className="w-full md:w-3/5 h-full flex items-center justify-center">
            <Doughnut data={chartData} options={options} />
          </div>

          {/* Legend */}
          <div className="w-full md:w-2/5 pl-0 md:pl-4 mt-4 md:mt-0">
            <div className="bg-gray-50 rounded-lg p-3">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Distribution
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {chartInfo.operationTypes.map((item, index) => (
                  <div
                    key={item.name}
                    className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100"
                    onClick={() => {
                      if (typeof onChartItemClick === "function") {
                        onChartItemClick({
                          type: "operationType",
                          value: item.name,
                          count: item.count,
                        });
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="flex items-center">
                      <div
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <span className="text-sm font-medium">{item.name}</span>
                    </div>
                    <div className="text-xs text-gray-600">
                      {item.count} ({item.percentage}%)
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chart Info Footer */}
      <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
        <div>
          <span>
            Total: <strong>{chartInfo.total}</strong>
          </span>
          <span className="mx-2">|</span>
          <span>
            Highest:{" "}
            <strong>
              {chartInfo.highest.key} ({chartInfo.highest.percentage}%)
            </strong>
          </span>
        </div>
        <div className="flex space-x-1">
          {/* These buttons could have actual functionality in a real implementation */}
          <button
            title="Download chart"
            className="p-1 hover:text-orange-600 transition-colors"
          >
            <FontAwesomeIcon icon={faDownload} />
          </button>
          <button
            title="Expand chart"
            className="p-1 hover:text-orange-600 transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
          <button
            title="Filter data"
            className="p-1 hover:text-orange-600 transition-colors"
          >
            <FontAwesomeIcon icon={faFilter} />
          </button>
        </div>
      </div>

      {/* Expanded View Modal (simplified implementation) */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setIsExpanded(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-4/5 h-4/5 max-w-6xl p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                References per Operation Type
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsExpanded(false)}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 h-5/6 gap-6">
              <div className="flex items-center justify-center">
                <Doughnut data={chartData} options={options} />
              </div>
              <div className="flex flex-col">
                <h4 className="text-md font-medium mb-4">
                  Operation Type Distribution
                </h4>
                <div className="bg-gray-50 rounded-lg p-4 overflow-y-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr>
                        <th className="py-2 text-left text-sm font-medium text-gray-700">
                          Operation Type
                        </th>
                        <th className="py-2 text-right text-sm font-medium text-gray-700">
                          Count
                        </th>
                        <th className="py-2 text-right text-sm font-medium text-gray-700">
                          Percentage
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {chartInfo.operationTypes.map((item, index) => (
                        <tr
                          key={item.name}
                          className="border-t border-gray-200"
                        >
                          <td className="py-3 text-sm">
                            <div className="flex items-center">
                              <div
                                className="w-3 h-3 rounded-full mr-2"
                                style={{ backgroundColor: item.color }}
                              ></div>
                              {item.name}
                            </div>
                          </td>
                          <td className="py-3 text-sm text-right">
                            {item.count}
                          </td>
                          <td className="py-3 text-sm text-right">
                            {item.percentage}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-gray-300">
                        <td className="py-3 text-sm font-medium">Total</td>
                        <td className="py-3 text-sm font-medium text-right">
                          {chartInfo.total}
                        </td>
                        <td className="py-3 text-sm font-medium text-right">
                          100%
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedReferencesPerOperationTypeDonutChart;
